import { pdsTv } from '@purinanbm/pds-ui/dist/common/pdsTv';
import { useLocation } from '@reach/router';
import { GatsbyImage, StaticImage } from 'gatsby-plugin-image';
import { sendIt } from 'gatsby-plugin-purina-analytics/common/functions';
import { merge } from 'lodash';
import * as React from 'react';
import { Link } from 'src/components/Link';
import { themeVariant } from 'src/theme/basic-styles';
import { getFocalPoint } from 'src/utils/mediaHelpers';
import { CMS_THEME } from '../../common/enums';
import ArticleOverlayCard from '../../components/article_card/ArticleOverlayCard';
import { StorageContentBlock } from '../common/StorageContentBlock/StorageContentBlock';

const theme = themeVariant();

const styles = pdsTv({
  slots: {
    cardsContainer:
      'pds-flex pds-w-full pds-max-w-[1360px] pds-flex-col pds-gap-5 pds-px-4 pds-py-6 sm:pds-p-6 [&_img]:pds-max-w-full',
    articleGrid: '',
    wrapper: 'pds-flex pds-items-center pds-justify-center',
  },
  variants: {
    theme: {
      dark: { wrapper: theme.dark },
      light: { wrapper: theme.light },
      neutral: { wrapper: theme.neutral },
    },
    grid: {
      true: {
        articleGrid: [
          'pds-grid pds-grid-cols-1 pds-gap-4.5 lg:pds-grid-flow-col lg:pds-grid-cols-[50%,50%] lg:pds-gap-0 lg:&first:pds-col-[1] lg:&first:*:pds-h-full',
        ],
      },
      false: { articleGrid: 'row' }, // bootstrap
    },
  },
});

interface StorageArticleCardsProps {
  node: IStorageArticleCards;
}

const getArticleCardProps = (node: IArticleContent) => {
  const { title, type, name, drupal_id } = node;
  const summary = node.body.summary ?? undefined;
  const url = node.path?.alias;
  // eslint-disable-next-line no-underscore-dangle
  const image = node.relationships.field_image?.relationships.field_media_image?.gatsbyImage;

  return {
    title,
    type,
    name,
    drupal_id,
    url,
    summary,
    imageComponent: image ? (
      <GatsbyImage
        aria-hidden="true"
        alt=""
        image={image}
        imgStyle={{
          objectFit: 'cover',
        }}
        className="lg:pds-h-full"
        objectPosition={getFocalPoint(node.relationships.field_image)}
      />
    ) : (
      <StaticImage
        aspectRatio={1.5}
        src="../assets/media/images/cat-dog-article-placeholder-2.jpg"
        aria-hidden="true"
        alt=""
      />
    ),
  };
};

const DefaultSettings = {
  entity_heading: {
    element: 'h2',
    style: 'typography_h2',
  },
  entity_variant: {
    theme: CMS_THEME.LIGHT,
  },
};
const StorageArticleCards: React.FC<StorageArticleCardsProps> = function ({
  node: cmsArticleCards,
}) {
  const location = useLocation();
  const settings = merge({}, DefaultSettings, cmsArticleCards?.behaviors);
  const node = { ...cmsArticleCards, behaviors: settings };
  const cmsTheme = settings.entity_variant.theme;
  const viewAll = cmsArticleCards?.field_links;

  const articleCards = cmsArticleCards.relationships.articles;
  let items;

  const handleGA = (article: { title: string; url: string }) => {
    sendIt({
      event: 'select_content',
      eventParams: {
        content_name: `article: ${article.title}`,
        content_type: 'article: article_card',
        cta_location: 'article',
        link_url: `${article.url} - ${location.hostname}${location.pathname}`,
      },
    });
  };
  if (articleCards.length > 2) {
    items = (
      <>
        <div data-id={articleCards[0].drupal_id} key={`0-${articleCards[0].drupal_internal__nid}`}>
          <ArticleOverlayCard
            cmsTheme={cmsTheme}
            {...getArticleCardProps(articleCards[0])}
            condensed={false}
          />
        </div>
        <div className="pds-flex pds-w-full pds-flex-col pds-gap-4.5 sm:pds-flex-row lg:pds-flex-col lg:pds-pl-4.5">
          {articleCards.slice(1, 3).map((article, index) => {
            return (
              <div
                data-id={article.drupal_id}
                className="pds-max-h-min"
                key={`${index}-${article.drupal_internal__nid}`}
              >
                <ArticleOverlayCard
                  cmsTheme={cmsTheme}
                  {...getArticleCardProps(article)}
                  condensed
                />
              </div>
            );
          })}
        </div>
      </>
    );
  } else {
    items = articleCards.map((article, index) => {
      return (
        <div
          data-id={article.drupal_id}
          key={`${index}-${article.drupal_internal__nid}`}
          className="col-12 col-md-6 pds-mb-4.5"
        >
          <ArticleOverlayCard
            cmsTheme={cmsTheme}
            {...getArticleCardProps(article)}
            grid={false} // grid false & condensed false only for 2 card layout
            condensed={false}
          />
        </div>
      );
    });
  }

  const stylesMemoed = React.useMemo(
    () =>
      styles({
        theme: cmsTheme,
        grid: articleCards.length === 3,
      }),
    [cmsTheme, articleCards.length],
  );

  const { cardsContainer, articleGrid, wrapper } = stylesMemoed;
  return (
    <div className={wrapper()}>
      <div
        id={cmsArticleCards.component_id}
        data-id={cmsArticleCards.drupal_id}
        className={cardsContainer()}
      >
        <div>
          <StorageContentBlock node={node} richTextSize="large" />
        </div>
        <div>
          <div className={articleGrid()}>{items}</div>
        </div>
        {viewAll && viewAll?.url && (
          <Link
            theme={cmsTheme}
            className="pds-inline pds-w-fit pds-underline"
            to={viewAll.url}
            analytics={false}
            onClick={() => handleGA({ title: viewAll.title, url: viewAll.url })}
            aria-label={viewAll.title}
          >
            {viewAll.title}
          </Link>
        )}
      </div>
    </div>
  );
};

export default StorageArticleCards;
